import { useState, useEffect } from 'react';
import styled from 'libs/styled';
import useAppDispatch from '@sportson/core-web/hooks/useAppDispatch';
import useBreakpoint from '@sportson/core-web/hooks/useBreakpoint';
import { show as showOverlay } from '@sportson/core-web/state/application/overlay';

import Text from 'components/Text';
import Row from '@sportson/core-web/App/layouts/Row';
import { PinIcon } from 'assets/media/icons/PinIcon';
import CoreButton from '@grebban/react-core-components/Button';
import useAppSelector from '@sportson/core-web/hooks/useAppSelector';
import { type IStore } from '@sportson/core-web/state/ecommerce/stores';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

const StoreSelectorButton = styled(CoreButton)`
    display: flex;
    width: 100%;
    padding: 12px 24px 12px 16px;
    justify-content: space-between;
    align-items: center;

    border: 1px solid transparent;
    border-radius: 4;
    background: var(--color-neutrals-100);

    &:hover {
        * {
            color: var(--color-base-black);
        }

        .last {
            text-decoration: underline;
        }
    }

    ${({ selected }) =>
        selected &&
        `
        border: 1px solid var(--color-base-black);
        background: var(--color-neutrals-100);
    }`}
`;

const StyledText = styled(Text)`
    font-family: 'Emeric';
    font-size: 12px;
    font-style: normal;
    font-weight: 450;
    line-height: 100%;
    color: var(--color-neutrals-500);

    display: flex;
    align-items: center;

    &.stock-status {
        &::after {
            content: '';
            display: inline-block;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            margin-left: 4px;
            background-color: var(--color-semantic-green);
            margin-bottom: 1px;
        }

        &.out {
            &::after {
                background-color: var(--color-semantic-yellow);
            }
        }
    }
`;

const StockDot = styled('div')``;
interface Props {
    label: string;
    selectedStore?: IStore;
    stockStatus?: number;
    inStockStore: boolean;
    onlyShowInStock: boolean;
    stores: any; // TODO: Update to proper type when CMS data is available
}

export const StoreSelector = ({ selectedStore, stockStatus, inStockStore, onlyShowInStock, stores }: Props) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [selected, setSelected] = useState(false);
    const isMobile = useBreakpoint({ to: 'desktop.sm' });

    const { name } = selectedStore || {};

    const handleOnClick = () => {
        dispatch(
            showOverlay({
                view: 'storeSelectorOverlay',
                transition: isMobile ? 'from-left' : 'from-right',
                data: { stores, onlyShowInStock, skipBasketItems: true },
            }),
        );
    };

    const stockClass = classNames({
        out: !inStockStore,
        'stock-status': !!selectedStore,
    });

    return (
        <StoreSelectorButton selected={selected} onClick={handleOnClick}>
            <Row gap="8px" alignItems="center" width="fit-content">
                <PinIcon color={selected ? 'var(--color-base-black)' : 'var(--color-neutrals-500)'} />
                <StyledText
                    className={stockClass}
                    selected={selected}
                    stockStatus={stockStatus}
                    overflow="hidden"
                    dangerouslySetInnerHTML={name ? { __html: name } : { __html: t('pdp.select_store') }}
                />
                {/* <StockDot /> */}
            </Row>
            <StyledText className="last" color="var(--color-neutrals-500)">
                {selectedStore ? t('pdp.change') : t('pdp.select')}
            </StyledText>
        </StoreSelectorButton>
    );
};
